import { useEffect, type ReactNode } from 'react';
import './theme/tokens.css';
import './theme/cursor.css';
import { useRoom } from './net/useRoom.js';
import { isEmbedded } from './discord/embedded.js';
import { useUiClickSounds } from './audio/useUiSounds.js';

/**
 * App — theme provider mount. Loads the design tokens (CSS custom properties)
 * app-wide and provides the dark base shell. Wave 1 A6 may extend this with a
 * per-phase theme context; the tokens import is the frozen contract.
 *
 * Also owns the ceremonial spearhead cursor (`rm-cursor` on <html>). It is on
 * for the whole Discord Activity — including the game-select gate, which now
 * renders hover-reactive mode cards (tilt + glare + teaser video) and looked
 * broken with the native arrow — and from the lobby onward everywhere else.
 * The public Store landing / join pages keep the native cursor.
 */
export function App({ children }: { children: ReactNode }) {
  const inRoom = useRoom((s) => s.publicView !== null);
  // App-wide UI click feedback (and the first-gesture audio unlock).
  useUiClickSounds();
  // isEmbedded() reads the host/query once at load; it cannot change without a
  // reload, so it needs no state — but it must be OR'd in here rather than
  // checked inside the effect, or the class would never apply before a room
  // exists (the exact gap that left the picker on the native cursor).
  const showCursor = inRoom || isEmbedded();
  useEffect(() => {
    document.documentElement.classList.toggle('rm-cursor', showCursor);
    return () => document.documentElement.classList.remove('rm-cursor');
  }, [showCursor]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-base)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      {children}
    </div>
  );
}

export default App;
